import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import cn from 'classnames';
import DataTable from '@containers/DataTable';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import Reason from '../components/Reason';
import { Column, Row } from '@components/Grid';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { createSumFromObject, formatDate } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import { getProblematicDisposalParcels, TProblematicDisposalParcel } from '@api/apiClient/problematic';
import { IDisposalTabProps } from '../types';
import { Modal } from '@components/Modal';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DisposalTab: React.FC<IDisposalTabProps> = (props: IDisposalTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    setIsFilterOpen
  } = props;

  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState<boolean>(false);
  const [parcelId, setParcelId] = useState<string>('');

  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();

  const onCloseParcel = () => {
    setIsOpenParcelWindow(false);
  };

  const columns: TableColumn<TProblematicDisposalParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcelId(row.data?.id);
          }}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.reason'),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) =>
          <Reason value={row.data.reason}/>,
      fixWidth: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.service'),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) => row.data.serviceName,
      fixWidth: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.price'),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {createSumFromObject(row.data.declaredValue)}
            </Text>
            {/*<Text*/}
            {/*    color={TextColor.DARK_MUTED}*/}
            {/*    size={TextSize.XS}*/}
            {/*>*/}
            {/*  {createSumFromObject(row.data.declaredValue)}*/}
            {/*</Text>*/}
          </div>
      ),
      alignment: 'right',
      width: '120px'
    },
    {
      header: t('problematicParcelsPage.seller'),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) => (
          <AvatarWithMarketplace
              id={row.data.sellerId}
              name={row.data.sellerName}
              marketplace={row.data.marketplaceType}
          />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t('problematicParcelsPage.decisionDate'),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) => formatDate(row.data.decisionWasMadeAt, 'dd.MM.yy'),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    },
    {
      header: t("disposalTable.disposed"),
      accessor: (row: TableRowType<TProblematicDisposalParcel>) => (
        <Text
          color={TextColor.PRIMARY}
          size={TextSize.S}
        >
          {(
            row.data.changeInitiator === 'Seller'
            || row.data.changeInitiator === 'SellerDashboard'
            || row.data.changeInitiator === 'WeChat'
          ) ? t('disposalTable.bySeller') : t('disposalTable.expired')}
        </Text>
      ),
      alignment: 'center',
      noOverflow: true
    },
  ], [currentLanguage]);

  return (
      <div>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="problematic-disposal"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackOrExternalNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'serviceIds',
                      type: FilterTypes.SERVICE_PROBLEMATIC
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    {
                      name: 'marketplaceType',
                      type: FilterTypes.MARKETPLACE
                    },
                    {
                      name: "changeInitiator",
                      type: FilterTypes.DISPOSED_BY,
                    },
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TProblematicDisposalParcel>
            columns={columns}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getProblematicDisposalParcels}
            notFound={t('problematicParcelsPage.noParcels')}
            pending={!isInitialized}
            queryKey="getProblematicDisposalParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={onCloseParcel}
            onClose={onCloseParcel}
            width={900}
        >
          <ParcelModalWindow parcelId={parcelId} isProblematic />
        </Modal>
      </div>
  );
};

export default DisposalTab;
